import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '../../common/Modal';
import { Input } from '../../common/Input';
import { Select } from '../../common/Select';
import { Button } from '../../common/Button';
import { SearchableSelect } from '../../common/SearchableSelect';
import type { Subscription } from '../../../types/subscriptionManagement';
import {
  DEPARTMENTS, CATEGORIES, BILLING_CYCLES, CURRENCIES,
  STATUSES, APPROVAL_STATUSES, APPROVED_BUDGETS,
} from '../../../types/subscriptionManagement';
import { optionsFromLookup } from '../../../utils/subscriptionLookupOptions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Subscription>) => Promise<void>;
  subscription: Subscription | null;
  vendors: string[];
  paymentMethods: string[];
  /** Google Sheet Lookups (column title → values); drives dropdowns when present. */
  lookupColumns?: Record<string, string[]>;
  /** Refetch snapshot (e.g. after editing Lookups in Sheets) without full-page blocking load. */
  refreshSnapshot?: () => void;
  saving: boolean;
  onAddVendor?: (vendor: string) => void;
  onAddDepartment?: (dept: string) => void;
  onAddCategory?: (cat: string) => void;
  employees?: string[];
}

function mergeExtras(base: string[], extras: string[]): string[] {
  const s = new Set(base.map(x => x.trim()).filter(Boolean));
  for (const x of extras) {
    const t = x.trim();
    if (t) s.add(t);
  }
  return [...s].sort((a, b) => a.localeCompare(b));
}

const APPROVED_BY_OPTIONS = ['Vinita Rathi', 'Nilesh Rathi'];
const toOpts = (arr: readonly string[]) => arr.map(v => ({ value: v, label: v }));

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parse YYYY-MM-DD (or ISO prefix); invalid/empty → null. Avoids Invalid Date + toISOString throws. */
function parseYmd(s: string | undefined): Date | null {
  if (!s || !String(s).trim()) return null;
  const t = String(s).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(y, mo - 1, day, 12, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isCustomBillingCycle(cycle: string | undefined): boolean {
  return String(cycle || '').trim().toLowerCase() === 'custom';
}

function addBillingFromStart(start: Date, billingCycle: string): string {
  if (isCustomBillingCycle(billingCycle)) return '';
  const d = new Date(start.getTime());
  const c = String(billingCycle || 'Monthly').toLowerCase();
  if (c === 'annual') d.setFullYear(d.getFullYear() + 1);
  else if (c === 'quarterly') d.setMonth(d.getMonth() + 3);
  else d.setMonth(d.getMonth() + 1);
  return formatYmd(d);
}

function getEmptyForm(): Partial<Subscription> {
  const start = parseYmd(todayYmd())!;
  return {
    name: '',
    vendor: '',
    department: 'Engineering',
    category: 'DevTools',
    owner: '',
    approvedBy: 'Vinita Rathi',
    billingCycle: 'Monthly',
    amount: undefined,
    currency: 'USD',
    startDate: formatYmd(start),
    renewalDate: addBillingFromStart(start, 'Monthly'),
    paymentMethod: '',
    approvedBudget: 'Monthly',
    status: 'Active',
    approvalStatus: 'Approved',
    notes: '',
  };
}

function sanitizeSubscriptionDates(sub: Partial<Subscription>): Partial<Subscription> {
  const out = { ...sub };
  let start = parseYmd(out.startDate as string);
  if (!start) {
    start = parseYmd(todayYmd())!;
    out.startDate = formatYmd(start);
  }
  if (!isCustomBillingCycle(out.billingCycle) && !parseYmd(out.renewalDate as string)) {
    out.renewalDate = addBillingFromStart(start, String(out.billingCycle || 'Monthly'));
  }
  return out;
}

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ label: string; color?: string }> = ({ label, color = 'bg-blue-500' }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className={`w-1 h-4 rounded-full ${color} shrink-0`} />
    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
  </div>
);

const searchInputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

// ─── Creatable search (Vendor / Department / Category) — same UX as invoice sub picker ─
interface CreatableFieldProps {
  label: string;
  value: string;
  options: string[];
  placeholder?: string;
  onChange: (v: string) => void;
  onAdd: (v: string) => void;
}
const CreatableField: React.FC<CreatableFieldProps> = ({
  label, value, options, placeholder = 'Search…', onChange, onAdd,
}) => {
  const [adding, setAdding] = useState(false);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.toLowerCase().includes(q));
  }, [options, query]);

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    onChange(trimmed);
    setInput('');
    setAdding(false);
  };

  const tryQuickAdd = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    onChange(trimmed);
    setQuery('');
    setOpen(false);
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {adding ? (
        <div className="flex gap-1.5">
          <input
            autoFocus
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAdd();
              if (e.key === 'Escape') { setAdding(false); setInput(''); }
            }}
            placeholder={`New ${label.replace(' *', '').toLowerCase()}…`}
            className={`flex-1 ${searchInputClass}`}
          />
          <Button size="sm" variant="primary" type="button" onClick={handleAdd}>Add</Button>
          <Button size="sm" variant="outline" type="button" onClick={() => { setAdding(false); setInput(''); }}>✕</Button>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <div ref={ref} className="relative flex-1 min-w-0">
            <input
              type="text"
              className={searchInputClass}
              value={open ? query : value}
              onChange={e => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => {
                setOpen(true);
                setQuery('');
              }}
              placeholder={value || placeholder}
              autoComplete="off"
            />
            {open && (
              <div
                className="absolute top-full mt-1 z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto"
                style={{ maxHeight: '200px' }}
              >
                {filtered.length > 0 ? (
                  filtered.map(v => (
                    <button
                      key={v}
                      type="button"
                      onMouseDown={() => {
                        onChange(v);
                        setQuery('');
                        setOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      {v}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-slate-400">
                    {options.length === 0 ? 'No options — use +' : 'No match'}
                  </div>
                )}
                {query.trim() && filtered.length === 0 && (
                  <button
                    type="button"
                    onMouseDown={() => tryQuickAdd(query)}
                    className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 border-t border-slate-100"
                  >
                    Add &quot;{query.trim()}&quot;
                  </button>
                )}
              </div>
            )}
          </div>
          <Button
            size="sm" variant="outline" type="button"
            className="shrink-0"
            onClick={() => setAdding(true)}
            title={`Add new ${label.replace(' *', '').toLowerCase()}`}
          >
            <Plus size={13} />
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Owner autocomplete ───────────────────────────────────────────────────────
interface OwnerFieldProps {
  value: string;
  employees: string[];
  onChange: (v: string) => void;
}
const OwnerField: React.FC<OwnerFieldProps> = ({ value, employees, onChange }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = employees.filter(e =>
    e.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div ref={ref} className="relative">
      <Input
        label="Owner"
        fullWidth
        value={open ? query : value}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        placeholder={value || 'Search employee…'}
      />
      {open && (
        <div className="absolute top-full mt-1 z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto" style={{ maxHeight: '160px' }}>
          {filtered.length > 0 ? (
            filtered.map(emp => (
              <button
                key={emp} type="button"
                onMouseDown={() => { onChange(emp); setQuery(''); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                {emp}
              </button>
            ))
          ) : (
            <div className="flex flex-col">
              <div className="px-3 py-2 text-xs text-slate-400">
                {employees.length > 0 ? 'No match found' : 'No employee list — type to enter name'}
              </div>
              {query && (
                <button
                  type="button"
                  onMouseDown={() => { onChange(query); setOpen(false); }}
                  className="text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 border-t border-slate-100"
                >
                  Use "{query}"
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
export const SubscriptionFormModal: React.FC<Props> = ({
  isOpen, onClose, onSave, subscription, vendors, paymentMethods, saving,
  lookupColumns = {},
  refreshSnapshot,
  onAddVendor, onAddDepartment, onAddCategory, employees = [],
}) => {
  const [form, setForm] = useState<Partial<Subscription>>(getEmptyForm);
  const [err, setErr] = useState('');
  const [localVendors, setLocalVendors] = useState<string[]>([]);
  const [deptExtras, setDeptExtras] = useState<string[]>([]);
  const [catExtras, setCatExtras] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      refreshSnapshot?.();
    }
  }, [isOpen, refreshSnapshot]);

  useEffect(() => {
    if (isOpen) {
      setForm(subscription ? sanitizeSubscriptionDates({ ...subscription }) : getEmptyForm());
      setErr('');
      setLocalVendors([...vendors]);
      setDeptExtras([]);
      setCatExtras([]);
    }
  }, [isOpen, subscription, vendors]);

  const departmentOptions = useMemo(
    () =>
      mergeExtras(
        optionsFromLookup('Department', lookupColumns, [...DEPARTMENTS], form.department),
        deptExtras,
      ),
    [lookupColumns, form.department, deptExtras],
  );
  const categoryOptions = useMemo(
    () =>
      mergeExtras(
        optionsFromLookup('Category', lookupColumns, [...CATEGORIES], form.category),
        catExtras,
      ),
    [lookupColumns, form.category, catExtras],
  );
  const billingCycleOptions = useMemo(
    () => optionsFromLookup('Billing Cycle', lookupColumns, [...BILLING_CYCLES], form.billingCycle),
    [lookupColumns, form.billingCycle],
  );
  const currencyOptions = useMemo(
    () => optionsFromLookup('Currency', lookupColumns, [...CURRENCIES], form.currency),
    [lookupColumns, form.currency],
  );
  const approvedBudgetOptions = useMemo(
    () => optionsFromLookup('Approved Budget', lookupColumns, [...APPROVED_BUDGETS], form.approvedBudget),
    [lookupColumns, form.approvedBudget],
  );
  const statusOptions = useMemo(
    () => optionsFromLookup('Status', lookupColumns, [...STATUSES], form.status),
    [lookupColumns, form.status],
  );
  const approvalStatusOptions = useMemo(
    () => optionsFromLookup('Approval Status', lookupColumns, [...APPROVAL_STATUSES], form.approvalStatus),
    [lookupColumns, form.approvalStatus],
  );
  const paymentMethodOptions = useMemo(
    () =>
      optionsFromLookup(
        'Payment Method',
        lookupColumns,
        paymentMethods,
        form.paymentMethod,
      ),
    [lookupColumns, paymentMethods, form.paymentMethod],
  );

  const set = (key: string, value: string | number) => {
    setForm(f => {
      const updated = { ...f, [key]: value };
      if (key === 'billingCycle') {
        const cycle = String(value);
        const start = parseYmd(f.startDate as string);
        if (start && !isCustomBillingCycle(cycle)) {
          updated.renewalDate = addBillingFromStart(start, cycle);
        } else if (isCustomBillingCycle(cycle) && !parseYmd(f.renewalDate as string) && start) {
          updated.renewalDate = addBillingFromStart(start, 'Monthly');
        }
      }
      if (key === 'startDate') {
        const start = parseYmd(value as string);
        if (start && !isCustomBillingCycle(f.billingCycle)) {
          updated.renewalDate = addBillingFromStart(start, String(f.billingCycle || 'Monthly'));
        }
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) { setErr('Subscription name is required'); return; }
    if (!form.vendor?.trim()) { setErr('Vendor is required'); return; }
    if (!form.amount || Number(form.amount) <= 0) { setErr('Amount must be greater than 0'); return; }
    if (isCustomBillingCycle(form.billingCycle)) {
      const start = parseYmd(form.startDate as string);
      const renewal = parseYmd(form.renewalDate as string);
      if (!renewal) {
        setErr('Renewal date is required when billing cycle is Custom');
        return;
      }
      if (start && renewal.getTime() <= start.getTime()) {
        setErr('Renewal date must be after start date');
        return;
      }
    }
    setErr('');
    try { await onSave(form); }
    catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Save failed'); }
  };

  const isEdit = !!subscription;
  const customBillingCycle = isCustomBillingCycle(form.billingCycle);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Subscription' : 'Add Subscription'}
      size="2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>{err && <p className="text-xs text-red-500">{err}</p>}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" type="button" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" type="button" onClick={handleSubmit} disabled={saving} isLoading={saving}>
              {isEdit ? 'Save Changes' : 'Create Subscription'}
            </Button>
          </div>
        </div>
      }
    >
      {/* Single scroll container — no double scrollbars */}
      <div className="space-y-5">

        {/* ── Section 1: Subscription Info ─────────────────────────────── */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3">
          <SectionHeader label="Subscription Info" color="bg-blue-500" />

          {/* Name + Vendor */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Name *"
              fullWidth
              value={form.name || ''}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. GitHub Enterprise"
            />
            <CreatableField
              label="Vendor *"
              value={form.vendor || ''}
              options={localVendors}
              placeholder="Select or add vendor…"
              onChange={v => set('vendor', v)}
              onAdd={v => { if (!localVendors.includes(v)) setLocalVendors(p => [...p, v]); onAddVendor?.(v); }}
            />
          </div>

          {/* Department + Category */}
          <div className="grid grid-cols-2 gap-3">
            <CreatableField
              label="Department"
              value={form.department || ''}
              options={departmentOptions}
              onChange={v => set('department', v)}
              onAdd={v => {
                if (!departmentOptions.includes(v)) setDeptExtras(p => [...p, v]);
                onAddDepartment?.(v);
              }}
            />
            <CreatableField
              label="Category"
              value={form.category || ''}
              options={categoryOptions}
              onChange={v => set('category', v)}
              onAdd={v => {
                if (!categoryOptions.includes(v)) setCatExtras(p => [...p, v]);
                onAddCategory?.(v);
              }}
            />
          </div>

          {/* Owner + Approved By */}
          <div className="grid grid-cols-2 gap-3">
            <OwnerField
              value={form.owner || ''}
              employees={employees}
              onChange={v => set('owner', v)}
            />
            <Select
              label="Approved By"
              fullWidth
              value={form.approvedBy || 'Vinita Rathi'}
              onChange={e => set('approvedBy', e.target.value)}
              options={toOpts(APPROVED_BY_OPTIONS)}
            />
          </div>
        </div>

        {/* ── Section 2: Billing & Payment ─────────────────────────────── */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3">
          <SectionHeader label="Billing & Payment" color="bg-emerald-500" />

          <Select
            label="Billing Cycle"
            fullWidth
            value={form.billingCycle || 'Monthly'}
            onChange={e => set('billingCycle', e.target.value)}
            options={toOpts(billingCycleOptions)}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Amount *"
              fullWidth
              type="number"
              min={0}
              step="0.01"
              value={form.amount === undefined || form.amount === null ? '' : form.amount}
              onChange={e =>
                set('amount', e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder="0"
            />
            <Select
              label="Currency"
              fullWidth
              value={form.currency || 'USD'}
              onChange={e => set('currency', e.target.value)}
              options={toOpts(currencyOptions)}
            />
          </div>

          {/* Start Date + Renewal Date */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Start Date"
              fullWidth
              type="date"
              value={form.startDate || ''}
              onChange={e => set('startDate', e.target.value)}
            />
            {customBillingCycle ? (
              <Input
                label="Renewal Date *"
                fullWidth
                type="date"
                value={form.renewalDate || ''}
                onChange={e => set('renewalDate', e.target.value)}
              />
            ) : (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                  Renewal Date
                  <span className="ml-1 text-[10px] font-normal text-slate-400">(auto-calculated)</span>
                </label>
                <input
                  type="date"
                  readOnly
                  value={form.renewalDate || ''}
                  title="Auto-calculated from start date and billing cycle"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500 cursor-default"
                />
              </div>
            )}
          </div>

          {/* Payment Method + Approved Budget */}
          <div className="grid grid-cols-2 gap-3">
            <SearchableSelect
              label="Payment Method"
              value={form.paymentMethod || ''}
              onChange={v => set('paymentMethod', v)}
              placeholder="Search payment method…"
              options={[
                { value: '', label: 'Select payment method…' },
                ...paymentMethodOptions.map(pm => ({ value: pm, label: pm })),
              ]}
            />
            <Select
              label="Approved Budget"
              fullWidth
              value={form.approvedBudget || 'Monthly'}
              onChange={e => set('approvedBudget', e.target.value)}
              options={toOpts(approvedBudgetOptions)}
            />
          </div>
        </div>

        {/* ── Section 3: Status & Notes ─────────────────────────────────── */}
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 space-y-3">
          <SectionHeader label="Status & Notes" color="bg-violet-500" />

          {/* Status + Approval Status */}
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Status"
              fullWidth
              value={form.status || 'Active'}
              onChange={e => set('status', e.target.value)}
              options={toOpts(statusOptions)}
            />
            <Select
              label="Approval Status"
              fullWidth
              value={form.approvalStatus || 'Approved'}
              onChange={e => set('approvalStatus', e.target.value)}
              options={toOpts(approvalStatusOptions)}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Notes</label>
            <textarea
              rows={2}
              value={form.notes || ''}
              onChange={e => set('notes', e.target.value)}
              placeholder="Optional — additional context, seat count, contract terms…"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            />
          </div>
        </div>

      </div>
    </Modal>
  );
};
